import type { ReactNode } from "react";
import { createT } from "#/lib/i18n.js";
import type { TemplateLayoutProps } from "#/lib/templates/types.js";
import {
  CommunicationPatternsTable,
  ContradictionsTimeline,
  CountryStatusMap,
  ExposureWindowTracker,
  FAQAccordion,
  Hero,
  KpiGrid,
  NewsFeed,
  Newswire,
  OfficialSourcesBlock,
  OutbreakMap,
  PathogenReferenceCard,
  PortEventsMap,
  Timeline,
  UnansweredQuestions,
  VesselLiveTrack,
  toMapRegions,
  toMapShip,
} from "./templateData.js";

function ProseSection({ children }: { children: ReactNode }) {
  return <section className="mx-auto w-full max-w-prose px-4 sm:px-6">{children}</section>;
}

function FullBleedSection({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-label={label} className="my-12 w-full px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">{children}</div>
    </section>
  );
}

export function TemplateEditorialLongform(props: TemplateLayoutProps) {
  const {
    outbreak,
    outbreaks,
    caseCounts,
    news,
    newsFeedMode,
    timeline,
    faqs,
    regions,
    ships,
    pathogen,
    outbreakSources,
    locale,
  } = props;
  const t = createT(locale, "landing");
  const mapRegions = toMapRegions(regions);
  const mapShip = toMapShip(ships);

  return (
    <main id="main-content" data-template-layout="editorial-longform">
      <div className="py-8">
        <div className="editorial-dropcap">
          <ProseSection>
            <Hero outbreak={outbreak} locale={locale} t={t} />
          </ProseSection>
        </div>

        <ProseSection>
          <div className="my-10 overflow-x-auto snap-x">
            <KpiGrid
              outbreak={outbreak}
              outbreaks={outbreaks}
              caseCounts={caseCounts}
              locale={locale}
              t={t}
            />
          </div>
        </ProseSection>

        <ProseSection>
          <PathogenReferenceCard
            pathogen={pathogen}
            locale={locale}
            t={createT(locale, "pathogen")}
          />
        </ProseSection>

        <ProseSection>
          <ExposureWindowTracker slug={outbreak.slug} locale={locale} t={t} />
        </ProseSection>

        {mapShip ? (
          <FullBleedSection id="vessel-live-track" label={createT(locale, "vesselTrack")("title")}>
            <VesselLiveTrack
              mmsi={mapShip.mmsi}
              locale={locale}
              t={createT(locale, "vesselTrack")}
            />
          </FullBleedSection>
        ) : null}

        <FullBleedSection id="map" label={t("section.map")}>
          <OutbreakMap regions={mapRegions} ship={mapShip} locale={locale} t={t} />
        </FullBleedSection>

        <FullBleedSection id="world-status-map" label={createT(locale, "worldMap")("title")}>
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <CountryStatusMap locale={locale} t={createT(locale, "worldMap")} />
            </div>
            <Newswire locale={locale} t={createT(locale, "worldMap")} />
          </div>
        </FullBleedSection>

        {mapShip ? (
          <FullBleedSection id="port-events" label={t("section.portEvents")}>
            <PortEventsMap vesselMmsi={mapShip.mmsi} t={createT(locale, "portEvents")} />
          </FullBleedSection>
        ) : null}

        <ProseSection>
          <Timeline slug={outbreak.slug} locale={locale} t={t} initialItems={timeline} />
        </ProseSection>
        <ProseSection>
          <CommunicationPatternsTable locale={locale} t={t} />
        </ProseSection>
        <ProseSection>
          <ContradictionsTimeline locale={locale} t={createT(locale, "contradictions")} />
        </ProseSection>
        <ProseSection>
          <UnansweredQuestions locale={locale} t={createT(locale, "contradictions")} />
        </ProseSection>
        <ProseSection>
          <NewsFeed
            slug={outbreak.slug}
            locale={locale}
            t={t}
            initialItems={news}
            feedMode={newsFeedMode}
          />
        </ProseSection>
        <ProseSection>
          <FAQAccordion slug={outbreak.slug} locale={locale} t={t} initialItems={faqs} />
        </ProseSection>
        <ProseSection>
          <OfficialSourcesBlock
            sources={outbreakSources ?? []}
            locale={locale}
            t={createT(locale, "pathogen")}
          />
        </ProseSection>
      </div>
    </main>
  );
}
